'use client'

import { useState } from 'react'
import { Bubble, Spacer, Text } from '@vinted/web-ui'
import classNames from 'classnames'

import {
  Message as MessageType,
  SafetyFlag,
} from '@marketplace-web/messaging/conversation-messaging-data'
import { UserPhoto } from '@marketplace-web/messaging/conversation-user-feature'
import { useConversationContext } from '@marketplace-web/messaging/conversation-providers-feature'
import { useTranslate } from '@marketplace-web/i18n/i18n-feature'
import { useSession } from '@marketplace-web/shared/session-data'
import { linkifyString } from '@marketplace-web/shared/url-util'

import { MEMBER_PROFILE_URL } from '../../constants/routes'
import DeletedMessage from '../DeletedMessage'
import MessageMenu from '../MessageMenu'
import MessageText from '../MessageText'
import MessageTime from '../MessageTime'
import EscrowWarningMessage from '../EscrowWarningMessage'

type Props = {
  isCurrentAuthor: boolean
  isLastAndRead: boolean
  isLast: boolean
  isGrouped: boolean
  createdTime: string
  isPortalMessage: boolean
  message: MessageType
}

const Message = ({
  isCurrentAuthor,
  isLastAndRead,
  isLast,
  isGrouped,
  createdTime,
  isPortalMessage,
  message,
}: Props) => {
  const translate = useTranslate('conversation.message')

  const { user } = useSession()

  const { conversation } = useConversationContext()
  const oppositeUser = conversation?.oppositeUser

  const [showTime, setShowTime] = useState(!isGrouped)

  const isTrustedMessage = oppositeUser?.isSystem && isPortalMessage

  const toggleTime = () => {
    if (!isGrouped) return

    setShowTime(prevValue => !prevValue)
  }

  const getMessageContainerAriaLabel = () => {
    if (message.isHidden) {
      return isCurrentAuthor
        ? translate('a11y.deleted_message_from_me')
        : translate('a11y.deleted_message_from_member', { member: oppositeUser?.login })
    }

    return isCurrentAuthor
      ? translate('a11y.message_from_me', { content: message.content })
      : translate('a11y.message_from_member', {
          member: oppositeUser?.login,
          content: message.content,
        })
  }

  const renderUserPhoto = () => {
    if (!oppositeUser || isGrouped) return null

    const { photo, id, isSystem } = oppositeUser

    return (
      <div className="u-ui-margin-right-regular u-inline-block u-float-left">
        <UserPhoto
          photo={photo}
          profileUrl={MEMBER_PROFILE_URL(id)}
          isSystem={isSystem}
          isCurrentAuthor={isCurrentAuthor}
          alt={translate('a11y.user_profile_picture', { username: oppositeUser.login })}
        />
      </div>
    )
  }

  const renderStatus = () => {
    if (!isCurrentAuthor || !isLastAndRead) return null

    return (
      <>
        <Spacer size={Spacer.Size.Small} />
        <div className="u-text-right">
          <Text
            as="h4"
            text={translate('is_read')}
            theme="muted"
            type={Text.Type.Caption}
            alignment={Text.Alignment.Right}
          />
        </div>
      </>
    )
  }

  const renderMessageTime = () => {
    if (!showTime) return null

    return <MessageTime time={createdTime} />
  }

  const renderEscrowWarning = () => {
    if (message.safetyFlag !== SafetyFlag.EscrowWarning) return null

    return (
      <EscrowWarningMessage
        currentUserId={user?.id}
        conversationId={conversation?.id}
        userSide={conversation?.transaction?.userSide}
        messageReplyId={message.id}
        messageSenderId={message.userId}
        messageRecipientId={message.userId === String(user?.id) ? oppositeUser?.id : user?.id}
        transactionId={conversation?.transaction?.id}
        isCurrentAuthor={isCurrentAuthor}
      />
    )
  }

  const renderMessageText = () => {
    const messageContainerAriaLabel = getMessageContainerAriaLabel()
    const messageText = isTrustedMessage ? (
      linkifyString(message.content)
    ) : (
      <>
        <MessageText
          text={message.content}
          conversationId={conversation?.id ? conversation.id.toString() : undefined}
        />
        {renderEscrowWarning()}
      </>
    )

    if (message.isHidden) {
      return (
        <DeletedMessage
          inverse={isCurrentAuthor}
          aria={{ 'aria-label': messageContainerAriaLabel }}
        />
      )
    }

    return (
      <Bubble inverse={isCurrentAuthor}>
        <Text
          as="span"
          theme="amplified"
          text={messageText}
          aria={{ 'aria-label': messageContainerAriaLabel }}
          html={isTrustedMessage}
          format
        />
      </Bubble>
    )
  }

  const renderMessageMenu = () => {
    if (message.isHidden) return null

    return <MessageMenu messageId={message.id} isLast={isLast} isCurrentAuthor={isCurrentAuthor} />
  }

  const messageContainerClass = classNames(
    'conversation-message-container',
    'u-ui-margin-top-medium',
    {
      'conversation-message-container__current-author': isCurrentAuthor,
      'conversation-message-container__grouped': isGrouped,
    },
  )

  const messageMenuContainerClass = classNames('conversation-message-menu-container', {
    'conversation-message-menu-container--reverse': !isCurrentAuthor,
  })

  return (
    <>
      {renderMessageTime()}
      <div className={messageContainerClass}>
        {renderUserPhoto()}
        <div className={messageMenuContainerClass}>
          {renderMessageMenu()}
          <div role="none" className="conversation-message" onClick={toggleTime}>
            {renderMessageText()}
            {renderStatus()}
          </div>
        </div>
      </div>
    </>
  )
}

export default Message
